import { forwardRef, useCallback, useId, useRef } from "react";
import type {
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler
} from "react";

import { Box, Input } from "@mantine/core";

import MentionListPortal from "./MentionListPortal";
import useMentionClipboard from "./useMentionClipboard";
import useMentionSuggestions from "./useMentionSuggestions";
import useSyncedMentionValue from "./useSyncedMentionValue";

import { DEFAULT_POPUP_HEIGHT, findEnabledOption, readValue } from "./utils";

import type { MentionTextInputProps } from "./types";

import classes from "./styles.module.css";

export type { MentionOption, MentionTextInputProps } from "./types";
export { default as MentionText } from "./MentionText";
export type { MentionTextProps } from "./MentionText";

const MentionTextInput = forwardRef<HTMLDivElement, MentionTextInputProps>(
  (props, forwardedRef) => {
    const {
      value,
      mentions,
      mask,
      onChange,
      onMentionSelect,
      onSubmit,
      onKeyDown,
      onFocus,
      onBlur,
      placeholder = "Write a message...",
      label,
      description,
      error,
      required,
      disabled,
      readOnly,
      id,
      className,
      maxDropdownHeight = DEFAULT_POPUP_HEIGHT,
      maxSuggestions = 8,
      "aria-label": ariaLabel
    } = props;

    const generatedId = useId();
    const inputId = id || `mention-input-${generatedId}`;
    const listId = `${inputId}-suggestions`;
    const editorRef = useRef<HTMLDivElement | null>(null);

    const setEditorRef = useCallback(
      (element: HTMLDivElement | null) => {
        editorRef.current = element;

        if (typeof forwardedRef === "function") {
          forwardedRef(element);
        } else if (forwardedRef) {
          forwardedRef.current = element;
        }
      },
      [forwardedRef]
    );

    const {
      activeMention,
      popupPosition,
      filteredMentions,
      highlightedIndex,
      setHighlightedIndex,
      closeSuggestions,
      updateSuggestions,
      handlePopupHeightChange,
      selectMention
    } = useMentionSuggestions({
      editorRef,
      mentions,
      mask,
      maxDropdownHeight,
      maxSuggestions,
      disabled,
      readOnly,
      onChange,
      onMentionSelect
    });

    useSyncedMentionValue(editorRef, value, mentions, mask);

    const { handleCopy, handleCut, handlePaste } = useMentionClipboard({
      editorRef,
      mentions,
      mask,
      disabled,
      readOnly,
      onChange,
      closeSuggestions
    });

    const handleInput = () => {
      const editor = editorRef.current;

      if (!editor) {
        return;
      }

      onChange(readValue(editor));
      updateSuggestions();
    };

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
      onKeyDown?.(event);

      if (event.defaultPrevented || event.nativeEvent.isComposing) {
        return;
      }

      if (activeMention && popupPosition) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          setHighlightedIndex(currentIndex =>
            findEnabledOption(
              filteredMentions,
              currentIndex,
              event.key === "ArrowDown" ? 1 : -1
            )
          );
          return;
        }

        if (
          (event.key === "Enter" || event.key === "Tab") &&
          highlightedIndex >= 0
        ) {
          event.preventDefault();
          selectMention(filteredMentions[highlightedIndex]);
          return;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          closeSuggestions();
          return;
        }
      }

      if (event.key === "Enter" && !event.shiftKey && onSubmit) {
        event.preventDefault();
        onSubmit();
      }
    };

    const handleBlur: FocusEventHandler<HTMLDivElement> = event => {
      closeSuggestions();
      onBlur?.(event);
    };

    const handleClick: MouseEventHandler<HTMLDivElement> = event => {
      if (event.detail !== 3) {
        updateSuggestions();
        return;
      }

      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNodeContents(event.currentTarget);
      selection?.removeAllRanges();
      selection?.addRange(range);
      closeSuggestions();
    };

    const activeOptionId =
      highlightedIndex >= 0
        ? `${listId}-option-${highlightedIndex}`
        : undefined;
    const descriptionId = description ? `${inputId}-description` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [descriptionId, errorId].filter(Boolean).join(" ");

    return (
      <Input.Wrapper
        label={label}
        description={description}
        error={error}
        required={required}
        className={className}
        labelProps={{ htmlFor: inputId }}
        descriptionProps={{ id: descriptionId }}
        errorProps={{ id: errorId }}
      >
        <Box
          ref={setEditorRef}
          component="div"
          id={inputId}
          className={classes.input}
          contentEditable={disabled || readOnly ? false : "plaintext-only"}
          suppressContentEditableWarning
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline="true"
          aria-autocomplete="list"
          aria-controls={activeMention ? listId : undefined}
          aria-activedescendant={activeOptionId}
          aria-expanded={Boolean(activeMention && popupPosition)}
          aria-describedby={describedBy || undefined}
          aria-disabled={disabled || undefined}
          aria-invalid={Boolean(error) || undefined}
          aria-readonly={readOnly || undefined}
          data-disabled={disabled || undefined}
          data-error={Boolean(error) || undefined}
          data-readonly={readOnly || undefined}
          data-empty={!value || undefined}
          data-placeholder={placeholder}
          tabIndex={disabled ? -1 : 0}
          onCopy={handleCopy}
          onCut={handleCut}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onKeyUp={event => {
            if (
              !["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(
                event.key
              )
            ) {
              updateSuggestions();
            }
          }}
          onClick={handleClick}
          onFocus={onFocus}
          onBlur={handleBlur}
          onPaste={handlePaste}
        />

        <MentionListPortal
          id={listId}
          options={filteredMentions}
          position={popupPosition}
          highlightedIndex={highlightedIndex}
          maxHeight={maxDropdownHeight}
          onHighlight={setHighlightedIndex}
          onHeightChange={handlePopupHeightChange}
          onSelect={selectMention}
        />
      </Input.Wrapper>
    );
  }
);

export default MentionTextInput;
